import { ChangelogPreview } from "@/components/changelog/ChangelogPreview";

import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import {
    badgeVariants,
    iconContainerVariants,
    iconVariants,
    sidebarButtonVariants,
} from "@/components/SidebarButton.variants";
import { useChangelog } from "@/hooks/use-changelog";
import { useFlagChangelogWidgetEnabled } from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { lazy, Suspense, useCallback, useState, type FC } from "react";
import { IconShipAnimated } from "@/assets/icons/IconShipAnimated";

/**
 * Notification badge with a ping pulse effect.
 * Renders a static dot with an expanding ring behind it.
 */
const NotificationBadge: FC = () => (
    <span className={cn(badgeVariants(), "size-1.5 overflow-visible")} role="status" aria-label="notification indicator">
        <span className="absolute inset-0 animate-badge-ping rounded-full bg-content-negative motion-reduce:animate-none" />
    </span>
);

const ChangelogDialog = lazy(() =>
    import("@/components/changelog/ChangelogDialog").then((m) => ({ default: m.ChangelogDialog })),
);

type ChangelogButtonProps = {
    isLocked: boolean;
};

/**
 * Ship icon that opens a changelog preview popover.
 * "Learn more" in the preview opens the full changelog dialog.
 * Gated behind the `changelog_widget_enabled` feature flag.
 */
export const ChangelogButton: FC<ChangelogButtonProps> = ({ isLocked }) => {
    const { value: featureEnabled } = useFlagChangelogWidgetEnabled();
    const { changelog, hasNewChangelog, markAsSeen } = useChangelog({ enabled: featureEnabled });
    const { isMobileViewport } = useIsMobileViewport();
    const [showPreview, setShowPreview] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [touched, setTouched] = useState(false);
    // In the collapsed sidebar the notification badge already signals new content,
    // so only animate the ship on hover/touch to avoid a persistent distracting bob.
    const shipAnimating = isLocked ? hovered || touched || hasNewChangelog : hovered || touched;

    const handleTouchStart = useCallback(() => {
        setTouched(true);
        // Let the animation play for one full cycle (1.6s) before stopping
        setTimeout(() => setTouched(false), 1600);
    }, []);

    const handlePreviewOpenChange = useCallback(
        (open: boolean) => {
            setShowPreview(open);
            if (open) {
                markAsSeen();
            }
        },
        [markAsSeen],
    );

    // On mobile, open the dialog directly. On desktop without a popover
    // (e.g. changelog not loaded yet), this also serves as the fallback.
    const handleClick = useCallback(() => {
        markAsSeen();
        if (isMobileViewport) {
            setShowDialog(true);
        } else {
            setShowPreview((prev) => !prev);
        }
    }, [markAsSeen, isMobileViewport]);

    const handleLearnMore = useCallback(() => {
        setShowPreview(false);
        setShowDialog(true);
    }, []);

    const handleDialogOpenChange = useCallback((open: boolean) => {
        setShowDialog(open);
    }, []);

    if (!featureEnabled) {
        return null;
    }

    const dialog =
        changelog && showDialog ? (
            <Suspense fallback={null}>
                <ChangelogDialog changelog={changelog} open={showDialog} onOpenChange={handleDialogOpenChange} />
            </Suspense>
        ) : null;

    // Collapsed sidebar: stacked icon.
    // We use a plain button instead of SidebarButton because SidebarButton
    // wraps in a Tooltip when hideLabel is set, which conflicts with the
    // Radix Popover trigger (nested asChild).
    if (!isLocked) {
        const collapsedIcon = (
            <button
                type="button"
                className={sidebarButtonVariants({ design: "new", state: "default" })}
                aria-label="What's new"
                data-tracking-id="open-changelog-collapsed"
                data-testid="changelog-button"
                onClick={handleClick}
            >
                <div className={iconContainerVariants({ design: "new" })}>
                    <span className={iconVariants({ design: "new" })} aria-hidden="true">
                        <IconShipAnimated size={20} animate={shipAnimating} continuous={false} />
                    </span>
                    {hasNewChangelog && <NotificationBadge />}
                </div>
            </button>
        );

        return (
            // Hover wrapper sits outside Radix/Tooltip so mouse events aren't intercepted
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onTouchStart={handleTouchStart}
            >
                {changelog && !isMobileViewport ? (
                    <ChangelogPreview
                        changelog={changelog}
                        open={showPreview}
                        onOpenChange={handlePreviewOpenChange}
                        onLearnMore={handleLearnMore}
                        side="right"
                    >
                        {collapsedIcon}
                    </ChangelogPreview>
                ) : (
                    <Tooltip content="What's new" side="right" usePortal>
                        {collapsedIcon}
                    </Tooltip>
                )}
                {dialog}
            </div>
        );
    }

    // Expanded sidebar: compact icon for inline placement
    const iconButton = (
        <button
            type="button"
            className={cn(sidebarButtonVariants({ design: "new", state: "default" }), "shrink-0")}
            aria-label="What's new"
            data-tracking-id="open-changelog-expanded"
            data-testid="changelog-button"
            onClick={handleClick}
        >
            <div className={iconContainerVariants({ design: "new" })}>
                <span className={iconVariants({ design: "new" })} aria-hidden="true">
                    <IconShipAnimated size={20} animate={shipAnimating} continuous={hasNewChangelog} />
                </span>
                {hasNewChangelog && <NotificationBadge />}
            </div>
        </button>
    );

    return (
        // Hover wrapper sits outside Radix/Tooltip so mouse events aren't intercepted
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onTouchStart={handleTouchStart}
        >
            {changelog && !isMobileViewport ? (
                <ChangelogPreview
                    changelog={changelog}
                    open={showPreview}
                    onOpenChange={handlePreviewOpenChange}
                    onLearnMore={handleLearnMore}
                >
                    {iconButton}
                </ChangelogPreview>
            ) : (
                <Tooltip content="What's new">{iconButton}</Tooltip>
            )}
            {dialog}
        </div>
    );
};
